import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, HelpCircle, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import api from '../../utils/api';

const PAISES = [
  { code: 'ES', name: 'España' },
  { code: 'PT', name: 'Portugal' },
  { code: 'FR', name: 'Francia' },
  { code: 'IT', name: 'Italia' },
  { code: 'DE', name: 'Alemania' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'NL', name: 'Países Bajos' },
  { code: 'BE', name: 'Bélgica' },
];

const MONEDAS = [
  { code: 'EUR', name: 'Euro (€)' },
  { code: 'USD', name: 'Dólar ($)' },
  { code: 'GBP', name: 'Libra (£)' },
];

const REGIMENES_IVA = [
  { value: 'general', label: 'Régimen General' },
  { value: 'recargo_equivalencia', label: 'Recargo de Equivalencia' },
  { value: 'exento', label: 'Exento de IVA' },
  { value: 'intracomunitario', label: 'Operador Intracomunitario' },
];

const TIPOS_IVA = [
  { value: '21', label: '21% (General)' },
  { value: '10', label: '10% (Reducido)' },
  { value: '4', label: '4% (Superreducido)' },
  { value: '0', label: '0% (Exento)' },
];

const FieldHelp = ({ text }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export default function FiscalDataStep({ initialData, onNext, onBack, saving }) {
  const [formData, setFormData] = useState(() => ({
    razon_social: initialData?.razon_social || '',
    nombre_comercial: initialData?.nombre_comercial || '',
    nif_cif: initialData?.nif_cif || '',
    pais_fiscal: initialData?.pais_fiscal || 'ES',
    domicilio_fiscal: initialData?.domicilio_fiscal || '',
    codigo_postal: initialData?.codigo_postal || '',
    ciudad: initialData?.ciudad || '',
    provincia: initialData?.provincia || '',
    moneda_principal: initialData?.moneda_principal || 'EUR',
    regimen_iva: initialData?.regimen_iva || 'general',
    tipo_iva_defecto: initialData?.tipo_iva_defecto || '21',
    aplica_retencion: initialData?.aplica_retencion || false,
    porcentaje_retencion: initialData?.porcentaje_retencion || '',
    intracomunitario: initialData?.intracomunitario || false,
    nif_vat_intra: initialData?.nif_vat_intra || '',
  }));
  const [errors, setErrors] = useState({});
  const [nifValidation, setNifValidation] = useState({ checked: false, valid: null });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // Reset NIF validation when NIF changes
    if (field === 'nif_cif') {
      setNifValidation({ checked: false, valid: null });
    }
  };

  const validateNIF = async () => {
    if (!formData.nif_cif) return;
    
    try {
      const response = await api.post('/validate/nif', { nif: formData.nif_cif });
      setNifValidation({ checked: true, valid: response.data.valid });
      if (!response.data.valid) {
        setErrors(prev => ({ ...prev, nif_cif: response.data.error }));
      }
    } catch (error) {
      console.error('Error validating NIF:', error);
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.razon_social || formData.razon_social.length < 3) {
      newErrors.razon_social = 'La razón social debe tener al menos 3 caracteres';
    }
    if (!formData.nif_cif || formData.nif_cif.length < 8) {
      newErrors.nif_cif = 'El NIF/CIF es obligatorio';
    }
    if (!formData.domicilio_fiscal || formData.domicilio_fiscal.length < 10) {
      newErrors.domicilio_fiscal = 'El domicilio fiscal debe tener al menos 10 caracteres';
    }
    if (!formData.codigo_postal) {
      newErrors.codigo_postal = 'El código postal es obligatorio';
    }
    if (!formData.ciudad || formData.ciudad.length < 2) {
      newErrors.ciudad = 'La ciudad es obligatoria';
    }
    if (formData.aplica_retencion && !formData.porcentaje_retencion) {
      newErrors.porcentaje_retencion = 'Indica el porcentaje de retención';
    }
    if (formData.intracomunitario && !formData.nif_vat_intra) {
      newErrors.nif_vat_intra = 'El NIF-VAT intracomunitario es obligatorio';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onNext(formData);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Datos Fiscales</h2>
        <p className="text-slate-600">Información fiscal de tu empresa para emisión de facturas</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Razón Social */}
        <div className="md:col-span-2">
          <Label className="flex items-center gap-2 mb-2">
            Razón Social <span className="text-red-500">*</span>
            <FieldHelp text="Nombre fiscal completo de la empresa tal como aparece en el registro mercantil" />
          </Label>
          <Input
            value={formData.razon_social}
            onChange={(e) => handleChange('razon_social', e.target.value)}
            placeholder="Ejemplo: Mi Empresa S.L."
            className={errors.razon_social ? 'border-red-500' : ''}
            data-testid="input-razon-social"
          />
          {errors.razon_social && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.razon_social}
            </p>
          )}
        </div>

        {/* Nombre Comercial */}
        <div className="md:col-span-2">
          <Label className="flex items-center gap-2 mb-2">
            Nombre Comercial
            <FieldHelp text="Nombre con el que opera la empresa (opcional si es igual a la razón social)" />
          </Label>
          <Input
            value={formData.nombre_comercial}
            onChange={(e) => handleChange('nombre_comercial', e.target.value)}
            placeholder="Opcional"
            data-testid="input-nombre-comercial"
          />
        </div>

        {/* NIF/CIF */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            NIF/CIF <span className="text-red-500">*</span>
            <FieldHelp text="Número de identificación fiscal (DNI, NIE o CIF)" />
          </Label>
          <div className="relative">
            <Input
              value={formData.nif_cif}
              onChange={(e) => handleChange('nif_cif', e.target.value.toUpperCase())}
              onBlur={validateNIF}
              placeholder="Ej: B12345678"
              className={`pr-10 ${errors.nif_cif ? 'border-red-500' : nifValidation.valid ? 'border-green-500' : ''}`}
              data-testid="input-nif-cif"
            />
            {nifValidation.checked && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {nifValidation.valid ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                )}
              </div>
            )}
          </div>
          {errors.nif_cif && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.nif_cif}
            </p>
          )}
        </div>

        {/* País Fiscal */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            País Fiscal <span className="text-red-500">*</span>
          </Label>
          <Select value={formData.pais_fiscal} onValueChange={(v) => handleChange('pais_fiscal', v)}>
            <SelectTrigger data-testid="select-pais-fiscal">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAISES.map(p => (
                <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Domicilio Fiscal */}
        <div className="md:col-span-2">
          <Label className="flex items-center gap-2 mb-2">
            Domicilio Fiscal <span className="text-red-500">*</span>
            <FieldHelp text="Dirección completa incluyendo calle, número, piso, puerta" />
          </Label>
          <Textarea
            value={formData.domicilio_fiscal}
            onChange={(e) => handleChange('domicilio_fiscal', e.target.value)}
            placeholder="Calle Ejemplo 123, 1º A"
            rows={2}
            className={errors.domicilio_fiscal ? 'border-red-500' : ''}
            data-testid="input-domicilio-fiscal"
          />
          {errors.domicilio_fiscal && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.domicilio_fiscal}
            </p>
          )}
        </div>

        {/* Código Postal */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Código Postal <span className="text-red-500">*</span>
          </Label>
          <Input
            value={formData.codigo_postal}
            onChange={(e) => handleChange('codigo_postal', e.target.value)}
            placeholder="28001"
            className={errors.codigo_postal ? 'border-red-500' : ''}
            data-testid="input-codigo-postal"
          />
          {errors.codigo_postal && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.codigo_postal}
            </p>
          )}
        </div>

        {/* Ciudad */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Ciudad <span className="text-red-500">*</span>
          </Label>
          <Input
            value={formData.ciudad}
            onChange={(e) => handleChange('ciudad', e.target.value)}
            placeholder="Madrid"
            className={errors.ciudad ? 'border-red-500' : ''}
            data-testid="input-ciudad"
          />
          {errors.ciudad && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.ciudad}
            </p>
          )}
        </div>

        {/* Provincia */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Provincia
          </Label>
          <Input
            value={formData.provincia}
            onChange={(e) => handleChange('provincia', e.target.value)}
            placeholder="Madrid"
            data-testid="input-provincia"
          />
        </div>

        {/* Moneda Principal */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Moneda Principal <span className="text-red-500">*</span>
          </Label>
          <Select value={formData.moneda_principal} onValueChange={(v) => handleChange('moneda_principal', v)}>
            <SelectTrigger data-testid="select-moneda">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONEDAS.map(m => (
                <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Régimen IVA */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Régimen IVA <span className="text-red-500">*</span>
            <FieldHelp text="El régimen fiscal determina cómo se aplican los impuestos en tus facturas" />
          </Label>
          <Select value={formData.regimen_iva} onValueChange={(v) => handleChange('regimen_iva', v)}>
            <SelectTrigger data-testid="select-regimen-iva">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REGIMENES_IVA.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tipo IVA por Defecto */}
        {formData.regimen_iva !== 'exento' && (
          <div>
            <Label className="flex items-center gap-2 mb-2">
              Tipo IVA por Defecto <span className="text-red-500">*</span>
            </Label>
            <Select value={formData.tipo_iva_defecto} onValueChange={(v) => handleChange('tipo_iva_defecto', v)}>
              <SelectTrigger data-testid="select-tipo-iva">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_IVA.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Aplica Retención */}
        <div className="md:col-span-2 space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="aplica_retencion"
              checked={formData.aplica_retencion}
              onCheckedChange={(checked) => handleChange('aplica_retencion', checked)}
              data-testid="checkbox-retencion"
            />
            <Label htmlFor="aplica_retencion" className="cursor-pointer">
              ¿Tu empresa aplica retención IRPF?
            </Label>
            <FieldHelp text="Común en profesionales y autónomos. La retención se descuenta de tus facturas." />
          </div>
          
          {formData.aplica_retencion && (
            <div className="ml-6 w-48">
              <Label className="flex items-center gap-2 mb-2">
                Porcentaje de Retención <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={formData.porcentaje_retencion}
                  onChange={(e) => handleChange('porcentaje_retencion', e.target.value)}
                  placeholder="15"
                  className={errors.porcentaje_retencion ? 'border-red-500' : ''}
                  data-testid="input-porcentaje-retencion"
                />
                <span className="text-slate-600">%</span>
              </div>
              {errors.porcentaje_retencion && (
                <p className="text-red-500 text-sm mt-1">{errors.porcentaje_retencion}</p>
              )}
            </div>
          )}
        </div>

        {/* Intracomunitario */}
        <div className="md:col-span-2 space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="intracomunitario"
              checked={formData.intracomunitario}
              onCheckedChange={(checked) => handleChange('intracomunitario', checked)}
              data-testid="checkbox-intracomunitario"
            />
            <Label htmlFor="intracomunitario" className="cursor-pointer">
              ¿Realizas operaciones intracomunitarias (UE)?
            </Label>
            <FieldHelp text="Si emites facturas a clientes en la Unión Europea, necesitarás un NIF-VAT" />
          </div>
          
          {formData.intracomunitario && (
            <div className="ml-6 w-64">
              <Label className="flex items-center gap-2 mb-2">
                NIF-VAT Intracomunitario <span className="text-red-500">*</span>
              </Label>
              <Input
                value={formData.nif_vat_intra}
                onChange={(e) => handleChange('nif_vat_intra', e.target.value.toUpperCase())}
                placeholder="ES12345678A"
                className={errors.nif_vat_intra ? 'border-red-500' : ''}
                data-testid="input-nif-vat"
              />
              {errors.nif_vat_intra && (
                <p className="text-red-500 text-sm mt-1">{errors.nif_vat_intra}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack} data-testid="btn-back">
          <ChevronLeft className="w-4 h-4 mr-2" /> Atrás
        </Button>
        <Button onClick={handleSubmit} disabled={saving} data-testid="btn-next">
          {saving ? 'Guardando...' : 'Continuar'} <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
